<template>
  <div class="contact-details-fi">
    <div
      v-if="address || isEditMode || inlineEditMode"
      class="contact-details-fi__address"
    >
      <icon-fi
        class="contact-details-fi__icon"
        icon="map-pin"
        size="1"
      />
      <multi-line-text-field-fi
        v-if="inlineEditMode || isEditMode"
        class="contact-details-fi__line"
        field-name="address"
      />
      <!-- eslint-disable vue/no-v-html -->
      <div
        v-else
        class="contact-details-fi__line"
        v-html="formatAddress(address)"
      />
      <!-- eslint-enable vue/no-v-html -->
    </div>
    <div v-if="whatsapp || isEditMode || inlineEditMode">
      <a
        :href="inlineEditMode || isEditMode ? undefined : getWhatsAppHref(whatsapp)"
        class="contact-details-fi__phone"
        :title="whatsapp"
      >
        <icon-fi
          class="contact-details-fi__icon"
          icon="whatsapp"
          size="1"
          icon-padding-24
        />
        <single-line-text-field-fi
          class="contact-details-fi__line"
          field-name="whatsapp"
        />
      </a>
    </div>
    <div v-if="phonenumber1 || isEditMode || inlineEditMode">
      <a
        :href="inlineEditMode || isEditMode ? undefined : getTelHref(phonenumber1)"
        class="contact-details-fi__phone"
        :title="phonenumber1"
      >
        <icon-fi
          class="contact-details-fi__icon"
          icon="phone-call"
          size="1"
        />
        <single-line-text-field-fi
          class="contact-details-fi__line"
          field-name="phonenumber1"
        />
      </a>
    </div>
    <div v-if="phonenumber2 || isEditMode || inlineEditMode">
      <a
        :href="inlineEditMode || isEditMode ? undefined : getTelHref(phonenumber2)"
        class="contact-details-fi__phone"
        :title="phonenumber2"
      >
        <icon-fi
          class="contact-details-fi__icon"
          icon="phone-call"
          size="1"
        />
        <single-line-text-field-fi
          class="contact-details-fi__line"
          field-name="phonenumber2"
        />
      </a>
    </div>
    <div v-if="fax || isEditMode || inlineEditMode">
      <a
        :href="inlineEditMode || isEditMode ? undefined : getFaxHref(fax)"
        class="contact-details-fi__fax"
        :title="fax"
      >
        <icon-fi
          class="contact-details-fi__icon"
          icon="printer"
          size="1"
        />
        <single-line-text-field-fi
          class="contact-details-fi__line"
          field-name="fax"
        />
      </a>
    </div>
    <div v-if="email || isEditMode || inlineEditMode">
      <a
        :href="inlineEditMode || isEditMode ? undefined : getMailToHref(email)"
        class="contact-details-fi__email"
        :title="email"
      >
        <icon-fi
          class="contact-details-fi__icon"
          icon="mail"
          size="1"
        />
        <single-line-text-field-fi
          v-if="inlineEditMode"
          class="contact-details-fi__line"
          field-name="email"
        />
        <!-- eslint-disable vue/no-v-html -->
        <div
          v-else
          class="contact-details-fi__line"
          v-html="insertBreaks(email)"
        />
        <!-- eslint-enable vue/no-v-html -->
      </a>
    </div>
    <div v-if="(isEditMode && fields?.website?.editable) || websiteHref || inlineEditMode">
      <a
        :href="inlineEditMode || isEditMode ? undefined : websiteHref"
        target="_blank"
        class="contact-details-fi__website"
        rel="noopener noreferrer"
        :title="websiteHref"
      >
        <icon-fi
          class="contact-details-fi__icon"
          icon="globe"
          size="1"
        />
        <!-- eslint-disable vue/no-v-html -->
        <span
          v-if="isEditMode"
          class="contact-details-fi__line"
          v-html="fields?.website?.editable"
        />
        <!-- eslint-enable vue/no-v-html -->
        <website-link-editor-fi
          v-else-if="inlineEditMode"
          :website-text="websiteText"
        />
        <span
          v-else
          class="contact-details-fi__line"
        >
          {{ websiteText }}
        </span>
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">
import { IconFi, MultiLineTextFieldFi, SingleLineTextFieldFi } from 'atoms';
import { computed, defineAsyncComponent, inject } from 'vue';

import { useInlineEdit } from '@/composables/inlineEdit';
import { getFaxHref, getMailToHref, getTelHref, getWhatsAppHref } from '@/composables/linkHelper';
import type { LinkField, SingleLineTextField } from '@/sitecoreFieldTypes';

defineOptions({
  name: 'ContactDetailsFi',
});

const fields = inject<{
  address?: SingleLineTextField;
  email?: SingleLineTextField;
  fax?: SingleLineTextField;
  whatsapp?: SingleLineTextField;
  phonenumber1?: SingleLineTextField;
  phonenumber2?: SingleLineTextField;
  website?: LinkField;
}>('fields');

const WebsiteLinkEditorFi = defineAsyncComponent(() => import('./WebsiteLinkEditorFi.vue'));

const { inlineEditMode } = useInlineEdit();

const address = computed(() => fields?.address?.value);
const email = computed(() => fields?.email?.value);
const fax = computed(() => fields?.fax?.value);
const whatsapp = computed(() => fields?.whatsapp?.value);
const phonenumber1 = computed(() => fields?.phonenumber1?.value);
const phonenumber2 = computed(() => fields?.phonenumber2?.value);

const websiteHref = computed(() => {
  const val = fields?.website?.value;
  if (typeof val === 'string') {
    return val;
  }
  return val?.href || '';
});

const websiteText = computed(() => {
  const val = fields?.website?.value;
  if (typeof val === 'string') {
    return val;
  }
  return val?.text || val?.href || '';
});

const formatAddress = (value = '') => value.replace(/\r?\n|\r/g, '<br>');

const insertBreaks = (value = '') => {
  let html = value
    .trim()
    .replace(
      / *\/ *| +/g,
      (match) => `</span>&#8203;${match}&#8203;<span class="contact-details-fi__line-item">`,
    );
  if (html.includes('@')) {
    html = html.replace(/ /g, '');
    html = value.replace(/@/, () => '</span>&#8203;<span class="contact-details-fi__line-item">@');
  }

  return `<span class="contact-details-fi__line-item">${html}</span>`;
};
</script>

<style scoped src="./contact-details-fi" />
